import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Plus, 
  Trash,
  Image as ImageIcon,
  Sparkle,
  UploadSimple
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HIDDEN_BUILTINS_KEY = 'dws_hidden_builtin_stickers';

const StickersPanel = ({ 
  onStickerSelect, 
  onClose, 
  userId,
  userRole = 'customer',
  inline = false  // when true → renders as an in-flow dock (no fixed overlay)
}) => {
  const [customStickers, setCustomStickers] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [uploadMode, setUploadMode] = useState('plain'); // 'plain' | 'gallery_ai'
  const [hiddenBuiltins, setHiddenBuiltins] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_BUILTINS_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryAIInputRef = useRef(null);

  useEffect(() => { loadCustomStickers(); }, []);

  const loadCustomStickers = async () => {
    try {
      const response = await axios.get(`${API}/stickers?user_id=${userId}`);
      setCustomStickers(response.data.stickers || []);
    } catch (error) { /* transient */ }
  };

  const persistHiddenBuiltins = (next) => {
    setHiddenBuiltins(new Set(next));
    try { localStorage.setItem(HIDDEN_BUILTINS_KEY, JSON.stringify([...next])); } catch {}
  };
  const hideBuiltinSticker = (id) => {
    const next = new Set(hiddenBuiltins); next.add(id); persistHiddenBuiltins(next);
    toast.success('Sticker hidden');
  };

  const handleStickerClick = (stickerUrl) => {
    onStickerSelect(stickerUrl);
    onClose();
  };

  const getStickerImageUrl = (sticker) => {
    // Sticker URL variants:
    //   • GCS/https: absolute URL — pass through as-is.
    //   • data:/blob: pass through as-is (built-in SVG stickers).
    //   • /api/... : prepend BACKEND_URL only.
    //   • /uploads/... : prepend BACKEND_URL + /api (routed via APIRouter).
    //   • Otherwise: fall back to old behaviour so nothing breaks.
    const url = sticker?.url || '';
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
    return `${BACKEND_URL}/api${url}`;
  };

  const handleUploadSticker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setShowCreateMenu(false);
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image should be under 5MB'); return; }

    const isGalleryAI = uploadMode === 'gallery_ai';
    setUploadMode('plain'); // reset for next time
    setIsUploading(true);
    if (isGalleryAI) toast.info('Removing background… please wait ✨');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);

      const endpoint = isGalleryAI ? `${API}/stickers/gallery-to-sticker` : `${API}/stickers/upload`;
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: isGalleryAI ? 90000 : 30000,
      });

      if (response.data.success) {
        if (isGalleryAI) {
          toast.success(response.data.bg_removed ? 'Sticker created — background removed ✨' : 'Sticker added (AI removal skipped)');
        } else {
          toast.success('Sticker added ✓');
        }
        loadCustomStickers();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      const msg = error?.response?.data?.detail || 'Failed to create sticker. Please try again.';
      toast.error(msg);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteSticker = async (stickerId) => {
    if (!window.confirm('Delete this sticker?')) return;
    try {
      await axios.delete(`${API}/stickers/${stickerId}?user_id=${userId}`);
      toast.success('Sticker deleted');
      loadCustomStickers();
    } catch (error) { toast.error('Failed to delete sticker'); }
  };

  const handleAIGenerate = async () => {
    const prompt = (aiPrompt || '').trim();
    if (!prompt) { toast.error('Enter a prompt'); return; }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/stickers/ai-generate`, { user_id: userId, prompt });
      if (res.data?.url) {
        toast.success('Sticker generated ✨');
        setAiPrompt('');
        setShowAIPrompt(false);
        setShowCreateMenu(false);
        loadCustomStickers();
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'AI generation failed');
    } finally { setAiGenerating(false); }
  };

  const canUpload = ['handler', 'admin', 'super_admin', 'deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'third_party_withdraw_handler'].includes(userRole);

  // Four hand-drawn SVG stickers baked into the app so they always work — no
  // upload / hosting needed. Data URLs stay <2KB each, decoded inline.
  const BUILT_IN_STICKERS = [
    {
      id: '__builtin_withdraw_done',
      builtin: true,
      label: 'Withdraw Done',
      url: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#22c55e"/><stop offset="1" stop-color="#15803d"/></linearGradient></defs><rect width="200" height="200" rx="28" fill="url(#g)"/><circle cx="100" cy="88" r="46" fill="#fff"/><path d="M78 88l16 16 30-32" fill="none" stroke="#16a34a" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/><text x="100" y="168" text-anchor="middle" font-family="Arial,sans-serif" font-size="24" font-weight="900" fill="#fff" letter-spacing="1">WITHDRAW DONE</text></svg>`),
    },
    {
      id: '__builtin_deposit_done',
      builtin: true,
      label: 'Deposit Done',
      url: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="d" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3b82f6"/><stop offset="1" stop-color="#1d4ed8"/></linearGradient></defs><rect width="200" height="200" rx="28" fill="url(#d)"/><circle cx="100" cy="88" r="46" fill="#fff"/><path d="M100 62 v40 M84 78 l16 -16 l16 16" fill="none" stroke="#1d4ed8" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/><rect x="70" y="106" width="60" height="8" rx="4" fill="#1d4ed8"/><text x="100" y="168" text-anchor="middle" font-family="Arial,sans-serif" font-size="26" font-weight="900" fill="#fff" letter-spacing="1">DEPOSIT DONE</text></svg>`),
    },
    {
      id: '__builtin_welcome',
      builtin: true,
      label: 'Welcome',
      url: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="w" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fde68a"/><stop offset="1" stop-color="#f59e0b"/></linearGradient></defs><rect width="200" height="200" rx="28" fill="url(#w)"/><circle cx="100" cy="70" r="22" fill="#fbbf24" stroke="#78350f" stroke-width="3"/><path d="M78 92 q22 -8 44 0 v34 h-44z" fill="#dc2626" stroke="#78350f" stroke-width="3"/><path d="M78 96 l-18 24 q-2 6 4 6 h20" fill="#fbbf24" stroke="#78350f" stroke-width="3" stroke-linejoin="round"/><path d="M122 96 l18 24 q2 6 -4 6 h-20" fill="#fbbf24" stroke="#78350f" stroke-width="3" stroke-linejoin="round"/><path d="M88 122 l12 -18 l12 18 z" fill="#fef3c7" stroke="#78350f" stroke-width="2"/><text x="100" y="176" text-anchor="middle" font-family="Arial,sans-serif" font-size="26" font-weight="900" fill="#78350f" letter-spacing="2">WELCOME</text></svg>`),
    },
    {
      id: '__builtin_refer_friend',
      builtin: true,
      label: 'Refer to Your Friend',
      url: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><linearGradient id="r" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ec4899"/><stop offset="1" stop-color="#a21caf"/></linearGradient></defs><rect width="200" height="200" rx="28" fill="url(#r)"/><circle cx="72" cy="72" r="20" fill="#fef3c7" stroke="#78350f" stroke-width="2.5"/><path d="M52 92 q20 -8 40 0 v22 h-40z" fill="#f59e0b" stroke="#78350f" stroke-width="2.5"/><circle cx="128" cy="72" r="20" fill="#fef3c7" stroke="#78350f" stroke-width="2.5"/><path d="M108 92 q20 -8 40 0 v22 h-40z" fill="#22c55e" stroke="#78350f" stroke-width="2.5"/><path d="M92 84 h16 M96 78 l-4 6 l4 6 M104 78 l4 6 l-4 6" stroke="#fff" stroke-width="3" stroke-linecap="round" fill="none"/><text x="100" y="146" text-anchor="middle" font-family="Arial,sans-serif" font-size="15" font-weight="900" fill="#fff" letter-spacing="1">REFER YOUR</text><text x="100" y="170" text-anchor="middle" font-family="Arial,sans-serif" font-size="20" font-weight="900" fill="#fef08a" letter-spacing="2">FRIEND ★</text></svg>`),
    },
  ];

  const displayStickers = [
    ...BUILT_IN_STICKERS.filter(s => !hiddenBuiltins.has(s.id)),
    ...customStickers.map(s => ({ ...s, url: getStickerImageUrl(s), builtin: false })),
  ];

  return (
    inline ? (
      <div className="bg-white w-full flex flex-col" data-testid="stickers-panel-inline">
        {/* Compact header with close */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-1.5">
            <ImageIcon size={18} className="text-green-600" />
            <h3 className="font-semibold text-sm" data-testid="stickers-panel-title">Stickers</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" data-testid="stickers-panel-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-3" style={{ maxHeight: '320px' }}>
          {renderPickerBody()}
        </div>
      </div>
    ) : (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-2xl max-h-[80vh] flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ImageIcon size={24} className="text-green-600" />
            <h3 className="font-semibold text-lg" data-testid="stickers-panel-title">Stickers</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="stickers-panel-close">
            <X size={24} />
          </button>
        </div>

        {/* Stickers Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderPickerBody()}
        </div>
      </div>
    </div>
    )
  );

  function renderPickerBody() {
    return (
      <>
          {/* Create Menu */}
          {canUpload && (
            <div className="mb-4 relative">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadSticker} className="hidden" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleUploadSticker} className="hidden" />
              <input ref={galleryAIInputRef} type="file" accept="image/*" onChange={handleUploadSticker} className="hidden" />
              <button
                onClick={() => setShowCreateMenu(v => !v)}
                disabled={isUploading}
                data-testid="stickers-create-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 transition-colors"
              >
                <Plus size={20} weight="bold" />
                {isUploading ? 'Uploading...' : 'Create Sticker'}
              </button>
              {showCreateMenu && (
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="stickers-create-menu">
                  <button
                    onClick={() => { setUploadMode('plain'); fileInputRef.current?.click(); }}
                    className="flex flex-col items-center gap-1 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200"
                    data-testid="stickers-create-upload"
                  >
                    <UploadSimple size={22} className="text-emerald-600" />
                    <span className="text-[11px] font-semibold text-slate-700">Upload Photo</span>
                  </button>
                  <button
                    onClick={() => { setUploadMode('plain'); cameraInputRef.current?.click(); }}
                    className="flex flex-col items-center gap-1 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200"
                    data-testid="stickers-create-camera"
                  >
                    <ImageIcon size={22} className="text-blue-600" />
                    <span className="text-[11px] font-semibold text-slate-700">Camera</span>
                  </button>
                  <button
                    onClick={() => { setUploadMode('gallery_ai'); galleryAIInputRef.current?.click(); }}
                    className="flex flex-col items-center gap-1 p-3 bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-xl border border-emerald-200"
                    data-testid="stickers-create-gallery-ai"
                    title="Pick a photo — AI will remove the background"
                  >
                    <div className="flex items-center gap-1">
                      <ImageIcon size={20} className="text-emerald-600" />
                      <Sparkle size={14} className="text-purple-600" weight="fill" />
                    </div>
                    <span className="text-[11px] font-semibold text-emerald-700 text-center leading-tight">Gallery → Sticker<br/><span className="text-[9px] text-emerald-600 font-normal">(BG removed)</span></span>
                  </button>
                  <button
                    onClick={() => { setShowAIPrompt(true); setShowCreateMenu(false); }}
                    className="flex flex-col items-center gap-1 p-3 bg-gradient-to-br from-fuchsia-50 to-purple-50 hover:from-fuchsia-100 hover:to-purple-100 rounded-xl border border-purple-200"
                    data-testid="stickers-create-ai"
                  >
                    <Sparkle size={22} className="text-purple-600" weight="fill" />
                    <span className="text-[11px] font-semibold text-purple-700">AI Generate</span>
                  </button>
                </div>
              )}
              {/* AI prompt inline modal */}
              {showAIPrompt && (
                <div className="mt-2 p-3 bg-gradient-to-br from-fuchsia-50 to-purple-50 border border-purple-200 rounded-xl" data-testid="stickers-ai-prompt">
                  <label className="text-[11px] font-bold uppercase tracking-wide text-purple-700 flex items-center gap-1 mb-1.5">
                    <Sparkle size={12} weight="fill" /> Describe your sticker
                  </label>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="e.g. cute cat waving hello with 'HI' text"
                    rows={2}
                    className="w-full text-sm p-2 rounded-lg border border-purple-200 focus:outline-none focus:border-purple-500 resize-none"
                    data-testid="stickers-ai-input"
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      onClick={() => { setShowAIPrompt(false); setAiPrompt(''); }}
                      disabled={aiGenerating}
                      className="px-3 py-1.5 text-[12px] text-slate-600 hover:bg-slate-100 rounded-lg"
                    >Cancel</button>
                    <button
                      onClick={handleAIGenerate}
                      disabled={aiGenerating || !aiPrompt.trim()}
                      className="px-3 py-1.5 text-[12px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                      data-testid="stickers-ai-submit"
                    >
                      {aiGenerating ? 'Generating…' : (<><Sparkle size={12} weight="fill" /> Generate</>)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stickers Grid (built-ins + custom)
              Latest-fix: web/desktop (lg ≥1024px) shows ~10 per row so
              stickers look small in the picker; mobile/PWA keeps grid-cols-3
              exactly as before (base class unchanged). */}
          <div className="grid grid-cols-3 gap-3 lg:grid-cols-10 lg:gap-2" data-testid="stickers-grid">
            {displayStickers.map((sticker) => (
              <div key={sticker.id} className="relative group">
                <button
                  onClick={() => handleStickerClick(sticker.url)}
                  className="w-full aspect-square bg-gray-100 rounded-xl overflow-hidden transition-all hover:ring-2 ring-green-300 active:scale-95"
                  data-testid={sticker.builtin ? `sticker-builtin-${sticker.id}` : `sticker-custom-${sticker.id}`}
                  title={sticker.label || 'Sticker'}
                >
                  <img
                    src={sticker.url}
                    alt={sticker.label || 'sticker'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
                {/* Delete for custom stickers OR hide for built-ins.
                    Built-in hide is per-user via localStorage — not global. */}
                {sticker.builtin ? (
                  canUpload && (
                    <button
                      onClick={(e) => { e.stopPropagation(); hideBuiltinSticker(sticker.id); }}
                      className="absolute -top-2 -right-2 bg-slate-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-lg"
                      data-testid={`sticker-hide-${sticker.id}`}
                      title="Hide this built-in sticker"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  )
                ) : canUpload && sticker.uploaded_by === userId && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSticker(sticker.id); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shadow-lg"
                    data-testid={`sticker-delete-${sticker.id}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Restore hidden built-ins */}
          {canUpload && hiddenBuiltins.size > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-center">
              <button
                onClick={() => persistHiddenBuiltins(new Set())}
                className="text-[11px] text-slate-500 underline hover:text-slate-700"
                data-testid="stickers-restore-hidden"
              >
                Restore {hiddenBuiltins.size} hidden built-in sticker{hiddenBuiltins.size === 1 ? '' : 's'}
              </button>
            </div>
          )}
      </>
    );
  }
};

export default StickersPanel;
